import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as entityGeneral from '../shared/models/general-models';

import { environment } from '@environment/environment';
import { BehaviorSubject, Observable, defer, filter, map, shareReplay, take, tap } from 'rxjs';
import CryptoJS from 'crypto-js';
import { Module } from '@app/layout/layout/modules.interface';
import { UserAuth, UserInfo } from '../shared/models/general-models';
import { EncryptionService } from '@app/shared/services/encryption.service';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public userSubject: BehaviorSubject<entityGeneral.User | null>;
  public user: Observable<entityGeneral.User | null>;

  /** Caché de `/usuario` por token: guards, layout y páginas comparten una sola petición por sesión. */
  private userInfoRequest$: Observable<UserInfo> | null = null;
  private userInfoRequestToken: string | null = null;
  private readonly userInfoSubject = new BehaviorSubject<UserInfo | null>(null);

  /**
   * Perfil del usuario (`/usuario`). Emite en cuanto está disponible (caché en storage o respuesta
   * del API), así las páginas no dependen de que `localStorage.userInfo` ya exista al iniciar.
   */
  public readonly userInfo$: Observable<UserInfo> = defer(() => {
    if (!this.userInfoSubject.value && this.userValue) {
      // Reusa la petición en vuelo (o la dispara); los errores los maneja el layout / interceptor
      this.getInfoUser().pipe(take(1)).subscribe({ error: () => undefined });
    }
    return this.userInfoSubject;
  }).pipe(filter((info): info is UserInfo => info !== null));

  constructor(private router: Router, private http: HttpClient, private encryptionService: EncryptionService) {
    this.userSubject = new BehaviorSubject(this.getDecryptedUser());
    this.user = this.userSubject.asObservable();
    this.userInfoSubject.next(this.getStoredUserInfo());
  }

  public get userValue() {
    return this.userSubject.value;
  }

  login(email: string, password: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
    };

    return this.http
      .post<UserAuth>(
        `${environment.API_URL_AUTH_V1}/login`,
        { email, password },
        httpOptions
      )
      .pipe(
        map((user) => {
          // Nueva sesión: el perfil cacheado (memoria + storage) pertenece a la anterior
          this.resetUserInfoCache();
          localStorage.removeItem('userInfo');
          const encryptedUser = CryptoJS.AES.encrypt(
            JSON.stringify(user.response),
            'secretKey'
          ).toString();
          localStorage.setItem('userEnergiaReal', encryptedUser);
          this.userSubject.next(user.response);
          return user;
        })
      );
  }

  register(data: any): Observable<any> {
    return this.http.post<any>(
      `${environment.API_URL_AUTH_V1}/${environment.APP}/registro`,
      data
    );
  }

  /** `/usuario` cacheado por token (shareReplay). Se invalida en `login()` y `clearSession()`. */
  getInfoUser(): Observable<UserInfo> {
    const dataUser = this.getDecryptedUser();
    const token: string | null = dataUser?.token ?? null;

    if (this.userInfoRequest$ && this.userInfoRequestToken === token) {
      return this.userInfoRequest$;
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    };

    this.userInfoRequestToken = token;
    this.userInfoRequest$ = this.http
      .get<UserInfo>(`${environment.API_URL_AUTH_V1}/usuario`, httpOptions)
      .pipe(
        tap((info) => {
          // Otra petición pudo responder 401 / cerrar sesión (o cambiar de sesión) mientras esta estaba en vuelo
          if (!this.userValue || this.userInfoRequestToken !== token) return;
          localStorage.setItem('userInfo', this.encryptionService.encryptData(info));
          this.userInfoSubject.next(info);
        }),
        // Los errores no se cachean: shareReplay reinicia la fuente y el siguiente suscriptor reintenta
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.userInfoRequest$;
  }

  getModules(): Observable<Module[]> {
    const dataUser = this.getDecryptedUser();

    if (!dataUser || !dataUser.userId) {
      console.error('User ID not found in decrypted user data.');
      return new Observable<Module[]>();
    }

    const userId = dataUser.userId;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${dataUser.token}`,
      }),
    };

    return this.http.get<Module[]>(
      `${environment.API_URL_AUTH_V1}/user/${userId}/modules`,
      httpOptions
    );
  }

  getDecryptedUser() {
    const encryptedUser = localStorage.getItem('userEnergiaReal');
    if (!encryptedUser) return null;

    try {
      const decryptedBytes = CryptoJS.AES.decrypt(encryptedUser, 'secretKey');
      return JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8)) || null;
    } catch (error) {
      // Sesión corrupta: si no se limpia, el constructor truena y la app queda en blanco (NG0200)
      console.error('Invalid stored session, clearing it.', error);
      this.clearSession();
      return null;
    }
  }

  /** Limpia la sesión completa (storage + estado en memoria). Única vía para cerrar sesión. */
  clearSession(): void {
    localStorage.removeItem('userEnergiaReal');
    localStorage.removeItem('userInfo');
    this.resetUserInfoCache();
    this.userSubject?.next(null);
  }

  private resetUserInfoCache(): void {
    this.userInfoRequest$ = null;
    this.userInfoRequestToken = null;
    this.userInfoSubject.next(null);
  }

  private getStoredUserInfo(): UserInfo | null {
    if (!this.userValue) return null;
    const stored = localStorage.getItem('userInfo');
    if (!stored) return null;
    const info: UserInfo | null = this.encryptionService.decryptData(stored);
    return info ?? null;
  }
}
